import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectSessionAdvisor } from '@/model';
import { licensesApi } from '@/features/licenses/api/licenses-api';
import { licenseKeys } from '@/api/waterlily';
import { useToast } from '@/components/hooks/use-toast';
import { CreateLicenseRequest, UpdateLicenseRequest, SetActiveLicenseRequest } from '@/features/licenses/types';

export function useLicenses() {
  const advisor = useSelector(selectSessionAdvisor);
  const advisorId = advisor?.advisorId || '';

  return useQuery({
    queryKey: licenseKeys.list({ advisorId }),
    queryFn: () => licensesApi.getLicenses(advisorId),
    enabled: !!advisorId,
  });
}

export function useSetActiveLicense() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const advisor = useSelector(selectSessionAdvisor);
  const advisorId = advisor?.advisorId || '';

  return useMutation({
    mutationFn: (data: SetActiveLicenseRequest) => licensesApi.setActiveLicense(advisorId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: licenseKeys.lists() });
      toast({
        title: 'Presenter updated',
        description: 'Your illustration presenter has been updated successfully.',
      });
    },
    onError: () => {
      toast({
        title: 'Failed to update presenter',
        description: 'Please try again or contact support if the issue persists.',
        variant: 'destructive',
      });
    },
  });
}

export function useCreateLicense() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const advisor = useSelector(selectSessionAdvisor);
  const advisorId = advisor?.advisorId || '';
  const { mutate: setActive } = useSetActiveLicense();

  return useMutation({
    mutationFn: (data: CreateLicenseRequest) => licensesApi.createLicense(advisorId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: licenseKeys.lists() });
      toast({
        title: 'Custom presenter created',
        description: 'Your custom presenter has been created successfully.',
      });
      // Automatically set as active if it's the first custom one or user intent? 
      // The original branch did this.
      setActive({ licenseId: data.license.licenseId });
    },
    onError: () => {
      toast({
        title: 'Failed to create presenter',
        description: 'Please try again or contact support if the issue persists.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateLicense() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const advisor = useSelector(selectSessionAdvisor);
  const advisorId = advisor?.advisorId || '';

  return useMutation({
    mutationFn: ({ licenseId, data }: { licenseId: string; data: UpdateLicenseRequest }) => 
      licensesApi.updateLicense(advisorId, licenseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: licenseKeys.lists() });
      toast({
        title: 'Presenter updated',
        description: 'Your custom presenter has been updated successfully.',
      });
    },
    onError: () => {
      toast({
        title: 'Failed to update presenter',
        description: 'Please try again or contact support if the issue persists.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteLicense() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const advisor = useSelector(selectSessionAdvisor);
  const advisorId = advisor?.advisorId || '';

  return useMutation({
    mutationFn: (licenseId: string) => licensesApi.deleteLicense(advisorId, licenseId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: licenseKeys.lists() });
      toast({
        title: 'Presenter deleted',
        description: data.switchedToAdvisorLicense 
          ? 'Presenter deleted. Reverted to "My License" as default.'
          : 'Your custom presenter has been deleted successfully.',
      });
    },
    onError: () => {
      toast({
        title: 'Failed to delete presenter',
        description: 'Please try again or contact support if the issue persists.',
        variant: 'destructive',
      });
    },
  });
}
